import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../../../../../hooks/useHandleAppException';
import { restApi } from '../../../../../../../../../services/api';
import snackbarStore from '../../../../../../../../../stores/Snackbar.store';
import { formatDateFromApi, formatDateToApi } from '../../../../../../../../../utils/dates';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../../../../../context/loadingStatus/LoadingStatus.store';
import { UsedInOfflineMode } from '../../../../../../../../offline/types/UsedInOfflineMode';
import { GetOrderConditionDTO } from '../../../../../../../conditions/dtos/orderCondition.dto';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import { orderConditionRepository } from '../../../../../../../conditions/orderCondition.repository';
import { OrderConditionSubmitData } from '../../../../../../../conditions/orderCondition.types';
import { useOrderCalculateContext } from '../../../../../../../contexts/OrderCalculate.context';
import { useOrderConditionContext } from '../../../../../../../contexts/OrderCondition.context';
import { validateOrderConditionsQuery } from '../../../../../../../contexts/order.utils';
import { Order } from '../../../../../../../order.types';
import useOrderConditionFieldsVisibility from '../../../hooks/useOrderConditionFieldsVisibility';
import { OrderConditionApplyToAllState } from '../../OrderConditionApplyToAllModal';
import {
  OrderConditionDetailsFormValues,
  orderConditionDetailsSchema,
} from '../orderConditionDetailsForm.schema';

interface UseOrderConditionDetailsFormParams extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
}

interface PropagableChange {
  field: PropagableField;
  value: OrderConditionDetailsFormValues[PropagableField];
}

interface UseOrderConditionDetailsFormResult {
  formMethods: UseFormReturn<OrderConditionDetailsFormValues>;
  save: () => Promise<void>;
  applyToAll: OrderConditionApplyToAllState;
}

const PROPAGABLE_FIELDS = [
  'paymentId',
  'commercialPolicyId',
  'deliveryAddressId',
  'shippingCostValue',
  'internalObservation',
  'observation',
] as const;

type PropagableField = (typeof PROPAGABLE_FIELDS)[number];

const getComparableValue = (value: unknown) => (value instanceof Date ? value.getTime() : value);

const getChangedFields = (
  values: OrderConditionDetailsFormValues,
  savedValues: OrderConditionDetailsFormValues,
) =>
  (Object.keys(values) as (keyof OrderConditionDetailsFormValues)[]).filter(
    (field) => getComparableValue(values[field]) !== getComparableValue(savedValues[field]),
  );

const parseOrderConditionDate = (date?: Date | null) => {
  const dateOnly = formatDateFromApi(date);

  return dateOnly ? parseISO(dateOnly) : null;
};

const buildDefaultValues = (condition: OrderCondition): OrderConditionDetailsFormValues => ({
  paymentId: condition.payment?.id ?? null,
  commercialPolicyId: condition.commercialPolicy?.id ?? null,
  voucherId: condition.businessPolicies.at(0)?.id ?? null,
  deliveryAddressId: condition.deliveryAddress?.id ?? null,
  shippingCostValue: condition.shippingCostValue ?? null,
  billingForecastDate: parseOrderConditionDate(condition.billingForecastDate),
  internalObservation: condition.internalObservation ?? null,
  observation: condition.observation ?? null,
});

const useOrderConditionDetailsForm = ({
  order,
  condition,
  isOfflineMode,
}: UseOrderConditionDetailsFormParams): UseOrderConditionDetailsFormResult => {
  const queryClient = useQueryClient();
  const { handleErrorResponse } = useHandleAppException();
  const { team } = useApplicationContext();
  const { orderConditions, updateOrderCondition, invalidateOrderConditions } =
    useOrderConditionContext();
  const { calculateSummary } = useOrderCalculateContext();
  const { handleMessage } = snackbarStore;
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const { isObservationVisible, isInternalObservationVisible } = useOrderConditionFieldsVisibility({
    order,
    condition,
    isOfflineMode,
  });
  const [pendingPropagation, setPendingPropagation] = useState<PropagableChange | null>(null);

  const notifyError = (error: Error) => {
    try {
      const message = handleErrorResponse({
        data: JSON.parse(error.message),
        ok: false,
      } as unknown as AppExceptionResponse);

      handleMessage({ message, type: 'error' });
    } catch {
      handleMessage({ message: error.message, type: 'error' });
    }
  };

  const formMethods = useForm<OrderConditionDetailsFormValues>({
    resolver: zodResolver(orderConditionDetailsSchema),
    defaultValues: buildDefaultValues(condition),
  });

  const savedValues = useRef(buildDefaultValues(condition));

  useEffect(() => {
    const conditionValues = buildDefaultValues(condition);

    savedValues.current = conditionValues;
    formMethods.reset(conditionValues);
  }, [condition.id]);

  const mutation = useMutation({
    scope: { id: `order-condition-${condition.id}` },
    networkMode: isOfflineMode ? 'always' : undefined,
    mutationFn: async () => {
      const values = formMethods.getValues();
      const hasVoucherChanged = values.voucherId !== savedValues.current.voucherId;

      const dto = {
        id: condition.id,
        paymentId: values.paymentId,
        commercialPolicyId: values.commercialPolicyId,
        ...(hasVoucherChanged && { businessPolicyIds: values.voucherId ? [values.voucherId] : [] }),
        deliveryAddressId: values.deliveryAddressId,
        shippingCostValue: values.shippingCostValue ?? 0,
        billingForecastDate: values.billingForecastDate
          ? formatDateToApi(values.billingForecastDate)
          : null,
        ...(isInternalObservationVisible
          ? { internalObservation: values.internalObservation }
          : {}),
        ...(isObservationVisible ? { observation: values.observation } : {}),
      } as unknown as Partial<OrderConditionSubmitData>;

      const response = await handleLoadingStatus({
        requestFn: () => orderConditionRepository(order.id, condition.id).update(dto),
      });

      return { response, submittedValues: values };
    },
    onSuccess: async ({ response, submittedValues }) => {
      savedValues.current = submittedValues;

      const updatedCondition = response?.data as GetOrderConditionDTO | undefined;

      if (updatedCondition) {
        updateOrderCondition(updatedCondition);
      } else {
        invalidateOrderConditions();
      }

      await Promise.all([
        calculateSummary(),
        queryClient.refetchQueries(validateOrderConditionsQuery(order.id, team?.id)),
      ]);
    },
    onError: (error: Error) => notifyError(error),
  });

  const propagateMutation = useMutation({
    scope: { id: `order-conditions-${order.id}` },
    mutationFn: async ({ field, value }: PropagableChange) => {
      await handleLoadingStatus({
        requestFn: () =>
          restApi.patch<void>(`orders/${order.id}/order-conditions`, { [field]: value }),
      });
    },
    onSuccess: async () => {
      invalidateOrderConditions();
      await Promise.all([
        calculateSummary(),
        queryClient.refetchQueries(validateOrderConditionsQuery(order.id, team?.id)),
      ]);
    },
    onError: notifyError,
  });

  const getPropagableChange = (
    values: OrderConditionDetailsFormValues,
    changedFields: (keyof OrderConditionDetailsFormValues)[],
  ): PropagableChange | null => {
    const field = PROPAGABLE_FIELDS.find((name) => changedFields.includes(name));

    if (!field) {
      return null;
    }

    const value = values[field];
    const isClearingValue = value === null || value === undefined || value === '';

    return isClearingValue ? null : { field, value };
  };

  const save = async () => {
    const values = formMethods.getValues();
    const changedFields = getChangedFields(values, savedValues.current);

    if (changedFields.length === 0) {
      return;
    }

    const isValid = await formMethods.trigger();

    if (!isValid) {
      return;
    }

    const propagableChange =
      orderConditions.length > 1 ? getPropagableChange(values, changedFields) : null;

    mutation.mutate();

    setPendingPropagation(propagableChange);
  };

  const applyToAll = () => {
    if (pendingPropagation) {
      propagateMutation.mutate(pendingPropagation);
    }

    setPendingPropagation(null);
  };

  return {
    formMethods,
    save,
    applyToAll: {
      isOpen: Boolean(pendingPropagation),
      isSaving: propagateMutation.isPending,
      onApplyToCurrent: () => setPendingPropagation(null),
      onApplyToAll: applyToAll,
    },
  };
};

export default useOrderConditionDetailsForm;
